/**
 * LIGHT COMMERCIAL ROUTING v1 — configuration only + safe surface mounts.
 *
 * NO /ljus hub. NO new SEO landings. NO frozen-path mutations.
 *
 * AUDIT (2026-09-08):
 * SAFE_TO_MUTATE: ownership seed, BlogPostPage next-steps, /ljud-ljus-skane,
 *   /kompletta-festpaket, ProductsPage Ljus use-case blurb (CRO), baseline JSON
 * BLOCKED_BY_FREEZE: /dj, /foretag, /eventteknik-foretag, /scen, /hyra-scen-malmo,
 *   /hyra-hogtalare-malmo
 * PROTECTED / WAITING: /ljud-ljus-brollop, /brollop (bröllop-teknik PREPARE — config
 *   ownership OK, no page mutation)
 */

export const LIGHT_COMMERCIAL_ROUTING_VERSION = 1;

export const LIGHT_ROUTING_BLOCKED_PATHS = [
  '/dj',
  '/foretag',
  '/eventteknik-foretag',
  '/scen',
  '/hyra-scen-malmo',
  '/hyra-hogtalare-malmo',
] as const;

/** Page mounts allowed for curated light ladder UI. */
export const LIGHT_LADDER_SAFE_MOUNT_PATHS = [
  '/ljud-ljus-skane',
  '/kompletta-festpaket',
] as const;

export const LIGHT_NAV_STATUS = 'WEAK' as const;
/** No header mutation in v1. */
export const LIGHT_NAV_MUTATION = 'NONE' as const;

export type LightGuideNextSteps = {
  lead: string;
  links: { to: string; label: string }[];
};

/**
 * Informational → commercial routing (BlogPostPage).
 * Blog remains informational owner; links point to existing money pages only.
 * Avoids frozen paths as primary commercial targets where alternatives exist.
 */
export const LIGHT_GUIDE_NEXT_STEPS: Record<string, LightGuideNextSteps> = {
  'nyhet-2025-09-24-hyra-ljud-och-ljus-till-fest-komplett-guide-2025': {
    lead:
      'Guiden ger översikt. Vill ni hyra ljus och ljud i stället för att pussla tekniken själva finns färdiga kommersiella vägar – utan att ni behöver välja varje armatur från början.',
    links: [
      { to: '/ljud-ljus-skane', label: 'Hyra ljud och ljus i Skåne' },
      { to: '/kompletta-festpaket', label: 'Kompletta festpaket' },
    ],
  },
  'nyhet-2025-08-05-s-skapar-du-rtt-stmning-med-ljud-och-ljus-vra-bsta-tips-fr-en-lyckad-fest': {
    lead:
      'När stämningen är klar i teorin: se konkreta ljud- och ljuslösningar ni kan boka i Skåne.',
    links: [
      { to: '/ljud-ljus-skane', label: 'Ljud & ljus Skåne' },
      { to: '/kompletta-festpaket', label: 'Festpaket med ljus' },
    ],
  },
};

export type LightLadderLevel = {
  id: 'stamning' | 'fest_dansgolv' | 'eventljus' | 'scen_produktion';
  title: string;
  summary: string;
  fits: string;
  /** Mounted levels only — level 4 is prepared */
  status: 'MOUNTED' | 'PREPARED_NOT_MOUNTED';
  productHref: string;
  productLabel: string;
  /** Optional secondary commercial link (must be freeze-safe) */
  secondaryHref?: string;
  secondaryLabel?: string;
};

/** Curated “ljus efter behov” — CRO/entity, not SEO prose. */
export const LIGHT_LADDER_LEVELS: readonly LightLadderLevel[] = [
  {
    id: 'stamning',
    title: 'Nivå 1 — Stämningsljus',
    summary: 'Uplights och diskret färgsättning av väggar, lokal och bakgrund.',
    fits: 'Middag, mingel, bröllop och mindre företagsevent.',
    status: 'MOUNTED',
    productHref: '/produkter/omgivningsbelysning-4-pack-uplights',
    productLabel: 'Se uplights',
    secondaryHref: '/produkter?category=Ljus',
    secondaryLabel: 'Alla ljusprodukter',
  },
  {
    id: 'fest_dansgolv',
    title: 'Nivå 2 — Fest & dansgolv',
    summary: 'Effektljus, partybars och enklare moving lights när dansgolvet ska leva.',
    fits: 'Fest, student, DJ och danskväll.',
    status: 'MOUNTED',
    productHref: '/produkter/mellan-ljuspaketet-effekt-fargsattning',
    productLabel: 'Se mellanljuspaketet',
    secondaryHref: '/kompletta-festpaket',
    secondaryLabel: 'Kompletta festpaket',
  },
  {
    id: 'eventljus',
    title: 'Nivå 3 — Eventljus',
    summary: 'Moving heads, strobes och starkare ljussättning för större ytor.',
    fits: 'Företagsevent, större fest och eventproduktion.',
    status: 'MOUNTED',
    productHref: '/produkter/moving-head-wash',
    productLabel: 'Se moving head wash',
    secondaryHref: '/produkter/kraftfull-vattentalig-350w-pixel-strobe',
    secondaryLabel: 'Se strobe',
  },
  {
    id: 'scen_produktion',
    title: 'Nivå 4 — Scen & produktion',
    summary: 'Professionell ljussättning tillsammans med scen, tross och tekniker.',
    fits: 'Produktioner som behöver rigg och bemanning.',
    status: 'PREPARED_NOT_MOUNTED',
    productHref: '/scen',
    productLabel: 'Scen (ej monterad under freeze)',
  },
] as const;

export const LIGHT_PRODUCT_USE_CASE_BLURBS: ReadonlyArray<{
  match: RegExp;
  title: string;
  blurb: string;
}> = [
  {
    match: /uplight|omgivningsbelysning/i,
    title: 'Uplights',
    blurb: 'För färgsättning av väggar, lokal och bakgrund.',
  },
  {
    match: /moving.?head/i,
    title: 'Moving heads',
    blurb: 'För rörligt scen- och dansgolvsljus.',
  },
  {
    match: /strobe|strobar/i,
    title: 'Strobes',
    blurb: 'För kraftfulla effekter på dansgolv och scen.',
  },
  {
    match: /party.?bar|gigbar|partybar|max-partybar|beamz/i,
    title: 'Partybar / effektljus',
    blurb: 'Kompakta effektlösningar när setup ska gå snabbt.',
  },
  {
    match: /ljuspaket/i,
    title: 'Ljuspaket',
    blurb: 'Färdiga lösningar när ni inte vill välja varje armatur själva.',
  },
];

export const LIGHT_ROUTING_CONTRACT = {
  version: LIGHT_COMMERCIAL_ROUTING_VERSION,
  no_ljus_hub: true as const,
  no_new_seo_landings: true as const,
  no_frozen_path_mutation: true as const,
  no_nav_mutation: true as const,
  no_blog_deopt: true as const,
  light_ladder_level4: 'PREPARED_NOT_MOUNTED' as const,
} as const;
